import { useCallback, useEffect, useRef, useState } from "react";
import { useFlags } from "flagsmith/react";
import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IGetPixByOrderService } from "@modules/orders/models/IGetPixByOrderService";
import { PixPayload } from "@pages/User/MyOrders/models/IPixPayment";
import { PixModalPaymentStatus } from "../types";

interface UsePixPaymentModalOptions {
  salesOrderNumber: string | null;
  enabled?: boolean;
  pollingInterval?: number;
}

interface UsePixPaymentModalReturn {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
  loading: boolean;
  refreshing: boolean;
  payload: PixPayload | null;
  paymentStatus: PixModalPaymentStatus;
  error: string | null;
  refreshQrCode: () => Promise<void>;
}

/**
 * Tenta ler um JSON do value da feature flag pix_modal_v2.
 * Se existir, usa como mock payload para testes manuais.
 *
 * Exemplo de value no Flagsmith:
 * {"status":"pendente","copiaECola":"00020126...","valor":31550}
 * ou
 * {"status":"pago","copiaECola":"00020126...","valor":31550,"transactionId":"TX-123","issueDate":"27/07/2026 10:00"}
 */
function useMockPayloadFromFlag(): PixPayload | null {
  const { pix_modal_v2 } = useFlags(["pix_modal_v2"]);
  const flagValue = pix_modal_v2?.value;

  if (!flagValue || typeof flagValue !== "string") return null;

  try {
    const parsed = JSON.parse(flagValue);
    if (parsed && typeof parsed === "object" && parsed.status) {
      return {
        main: true,
        copiaECola: parsed.copiaECola || "",
        valor: parsed.valor || 0,
        status: parsed.status,
        transactionId: parsed.transactionId,
        issueDate: parsed.issueDate,
        ...parsed,
      } as PixPayload;
    }
  } catch {
    // value não é JSON válido — ignora
  }
  return null;
}

export function usePixPaymentModal({
  salesOrderNumber,
  enabled = true,
  pollingInterval = 15000,
}: UsePixPaymentModalOptions): UsePixPaymentModalReturn {
  const iocContext = useIoCContext();
  // O container resolve a dependência em escopo transient (nova instância a
  // cada .get()), então guardamos numa ref para não recriar fetchPix/o
  // polling a cada render.
  const getPixByOrderServiceRef = useRef<IGetPixByOrderService>(
    iocContext.serviceContainer.get<IGetPixByOrderService>(
      Types.Orders.IGetPixByOrderService
    )
  );

  const mockPayload = useMockPayloadFromFlag();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [payload, setPayload] = useState<PixPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchPix = useCallback(
    async (isRefresh = false) => {
      if (!salesOrderNumber) return;

      // Se tem mock da feature flag, usa direto sem chamar API
      if (mockPayload) {
        setPayload(mockPayload);
        setError(null);
        return;
      }

      try {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        const result = await getPixByOrderServiceRef.current.execute(
          salesOrderNumber
        );
        // Pega o payload principal (main = true) ou o primeiro
        const mainPayload = result.find((p) => p.main) || result[0] || null;
        setPayload(mainPayload);
        setError(null);
      } catch (err: any) {
        setError(err.message ?? "Erro ao carregar dados do Pix");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [salesOrderNumber, mockPayload]
  );

  const paymentStatus: PixModalPaymentStatus =
    payload?.status === "pago" ? "confirmed" : "pending";

  const openModal = useCallback(() => {
    setOpen(true);
    if (enabled && salesOrderNumber) {
      fetchPix(false);
    }
  }, [enabled, salesOrderNumber, fetchPix]);

  const closeModal = useCallback(() => {
    setOpen(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const refreshQrCode = useCallback(async () => {
    await fetchPix(true);
  }, [fetchPix]);

  // Polling para verificar status de pagamento enquanto modal está aberto
  // Desabilita polling quando usando mock (não precisa)
  useEffect(() => {
    if (
      open &&
      paymentStatus === "pending" &&
      salesOrderNumber &&
      !mockPayload
    ) {
      pollingRef.current = setInterval(() => {
        fetchPix(true);
      }, pollingInterval);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [
    open,
    paymentStatus,
    salesOrderNumber,
    pollingInterval,
    fetchPix,
    mockPayload,
  ]);

  return {
    open,
    openModal,
    closeModal,
    loading,
    refreshing,
    payload,
    paymentStatus,
    error,
    refreshQrCode,
  };
}
